import * as IoC from 'typescript-ioc';
import { AppPrivateConfigurationDTO, AppPublicConfigurationDTO, IAppConfigurationDAO } from '../../../api/app-configuration.dao';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api';
import Amplify from '@aws-amplify/core';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import { IUserDetailsDTO } from '../../../api/users.dao';

@IoC.Singleton
export class AuthServiceConfigurator {
    private trackingServiceProvider: TrackingServiceProvider;
    private publicConfiguration: AppPublicConfigurationDTO;
    private privateConfiguration: AppPrivateConfigurationDTO;

    constructor(
        @IoC.Inject private readonly appConfigurationDao: IAppConfigurationDAO = IoC.Container.get(IAppConfigurationDAO),
    ) {
        this.trackingServiceProvider = IoC.Container.get(TrackingServiceProvider);
    }

    public async configureAmplify(): Promise<void> {
        const config = await this.getPrivateConfiguration();
        this.configureAmplifyAppSync(config);
    }

    public async configureTrackingService(sessionToken: string, user: IUserDetailsDTO): Promise<void> {
        const { mixPanelToken } = await this.getPrivateConfiguration();
        const trackingService = this.trackingServiceProvider.get();
        trackingService.initialize(mixPanelToken, sessionToken);
        trackingService.identify(user.username);
        trackingService.peopleSet(user);
    }

    private configureAmplifyAppSync(config: AppPrivateConfigurationDTO): void {
        Amplify.configure({
            aws_project_region: config.region,
            aws_appsync_region: config.region,
            aws_appsync_graphqlEndpoint: `${config.appSyncUrl}/realtime`, // Add WS endpoint in common setting as OE Central is using only subscriptions for now
            aws_appsync_apiKey: config.appSyncKey,
            aws_appsync_authenticationType: GRAPHQL_AUTH_MODE.AWS_LAMBDA
        });
    }

    private async getPublicConfiguration(): Promise<AppPublicConfigurationDTO> {
        if (!this.publicConfiguration) await this.initPublicConfiguration();
        return this.publicConfiguration;
    }
    private async initPublicConfiguration(): Promise<void> {
        try {
            this.publicConfiguration = await this.appConfigurationDao.getPublicConfiguration();
        } catch (err) {
            throw new Error(`Failed to fetch app public configuration: ${err.message}`);
        }
    }

    private async getPrivateConfiguration(): Promise<AppPrivateConfigurationDTO> {
        if (!this.privateConfiguration) await this.initPrivateConfiguration();
        return this.privateConfiguration;
    }
    private async initPrivateConfiguration(): Promise<void> {
        try {
            this.privateConfiguration = await this.appConfigurationDao.getPrivateConfiguration();
        } catch (err) {
            throw new Error(`Failed to fetch app private configuration: ${err.message}`);
        }
    }

    public async getFusionAuthClientId(): Promise<string> {
        const config = await this.getPublicConfiguration();
        return (config && config.fusionAuthClientId) ? config.fusionAuthClientId : null;
    }
}
